if (!customElements.get("wt-drawer-content")) {
  customElements.define(
      "wt-drawer-content",
    class DrawerContent extends HTMLElement {
      constructor() {
        super();

        this.drawerClass = "wt-drawer-content__drawer";
        this.classDrawerActive = `${this.drawerClass}--open`;
        this.pageOverlayClass = "wt-drawer-content__overlay";
        this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;

        this.isOpen = false;
        this.lastTrigger = null;
        this.drawerId = null;

        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleOverlayClick = this.handleOverlayClick.bind(this);
      }

      connectedCallback() {

        this.drawerId = this.dataset.drawerId;
        this.init();

        const jsonScript = this.querySelector("[data-variants-metafields]");
        if (jsonScript) {
          this.variantJson = JSON.parse(jsonScript.textContent);
          window.addEventListener("variantChangeEnd", this.handleVariantChange);
        }
      }


      disconnectedCallback() {
        document.removeEventListener("keydown", this.handleKeydown);
        window.removeEventListener("variantChangeEnd", this.handleVariantChange);
        this.cleanup();
      }

      cleanup() {
        this.triggers = this.querySelectorAll(
          `.wt-drawer-content__trigger`
        );
        this.triggers.forEach((trigger) => {
            trigger.removeEventListener("click", this.handleTriggerClick);
        });

        const closeBtn = document.querySelector(".wt-drawer-content__drawer__close");
        if (closeBtn) {
          closeBtn.removeEventListener("click", this.handleTriggerClick);
        }

        const overlay = this.getOverlay();
        if (overlay) {
          overlay.removeEventListener("click", this.handleOverlayClick);
        }
      }

      handleKeydown(e) {
        if (!this.isOpen) return;

        const isEsc = e.key === "Escape";
        const isTab = e.key === "Tab";

        if (isEsc) {
          e.preventDefault();
          this.closeDrawer();
        } else if (isTab) {
          this.handleFocusTrap(e);
        }
      }

      handleFocusTrap(e) {
        const { first, last } = this.getFocusableElements();

        if (!first || !last) return;

        const activeElement = document.activeElement;
        const isWithinDrawer = this.drawer.contains(activeElement);

        if (!isWithinDrawer) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
          return;
        }

        if (e.shiftKey && activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      handleVariantChange(e) {
        const variantId = String(e.target.currentVariant.id);
        const currentVariantInfo = this.variantJson?.variants?.find(
          (el) => el.id === variantId,
        );
        if (!currentVariantInfo) return;

        for (const metafield of currentVariantInfo.metafields) {
          const { placeholder_name, value } = metafield;
          const els = this.querySelectorAll(
            `[data-variant-metafield="${placeholder_name}"]`,
          );
          els.forEach((el) => {
            el.textContent = value || "";
          });
        }
      }

      handleTriggerClick(e) {
        e.preventDefault();
        const trigger = e.currentTarget;

        const isOpeningTrigger = trigger.classList.contains("wt-drawer-content__trigger");
        const isClosingTrigger = trigger.classList.contains("wt-drawer-content__drawer__close");
        const isKeyboardActivation = e.detail === 0;

        if (isOpeningTrigger) {
          this.setLastTrigger(trigger);
          this.updateDrawerContent(trigger);
          this.openDrawer();
        } else if (isClosingTrigger) {
          this.closeDrawer();
        }

        this.updateCloseBtnFocusState(e.detail);
      }

      handleOverlayClick(e) {
        e.preventDefault();
        document.querySelector('wt-drawer-content[open]')?.closeDrawer();
      }

      setLastTrigger(trigger) {
        const isFocusable = trigger.matches(
          'button, a[href], [tabindex]:not([tabindex="-1"])'
        );

        if (isFocusable) {
          this.lastTrigger = trigger;
        } else {
          const focusableChild = trigger.querySelector(
            'button, a[href], [tabindex]:not([tabindex="-1"])'
          );
          this.lastTrigger = focusableChild || trigger;
        }
      }

      updateCloseBtnFocusState(clickDetail) {
        const closeBtn = document.querySelector(".wt-drawer-content__drawer__close");
        if (!closeBtn) return;

        if (clickDetail > 0) {
          closeBtn.classList.add("remove-focus");
        } else {
          closeBtn.classList.remove("remove-focus");
        }
      }

      getFocusableElements() {
        const focusableSelector = "button, [href], input, select, [tabindex]";
        const focusableElements = Array.from(
          this.drawer.querySelectorAll(focusableSelector)
        ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);

        return {
          first: focusableElements[0],
          last: focusableElements[focusableElements.length - 1],
        };
      }

      temporaryHideFocusVisible() {
        document.body.classList.add("no-focus-visible");
        setTimeout(() => {
          document.body.classList.remove("no-focus-visible");
        }, 200);
      }

      getOverlay() {
        return document.querySelector(`.${this.pageOverlayClass}`);
      }

      openDrawer() {
        this.setAttribute("open", "");
        this.isOpen = true;
        this.drawer.removeAttribute("inert");
        this.drawer.setAttribute("aria-hidden", "false");
        this.drawer.classList.add(this.classDrawerActive);
        document.body.classList.add(this.activeOverlayBodyClass);
        document.addEventListener("keydown", this.handleKeydown);
        this.closeBtn = document.querySelector(".wt-drawer-content__drawer__close");
        if(this.closeBtn) {
           this.closeBtn.addEventListener("click", this.handleTriggerClick);
           // Focusing forces a style and layout pass over the whole page, so it
           // waits until the open drawer has been painted (INP).
           const closeBtn = this.closeBtn;
           requestAnimationFrame(() => {
             setTimeout(() => {
               if (this.isOpen) closeBtn.focus({ preventScroll: true });
             });
           });
        }

        if (this.lastTrigger) {
          this.lastTrigger.setAttribute("aria-expanded", "true");
        }

        const overlay = this.getOverlay();
        if (overlay) {
          overlay.setAttribute("aria-hidden", "false");
        }

        this.temporaryHideFocusVisible();
      }

      updateDrawerContent(trigger){
        const blockId = trigger.dataset.drawerId;

        // textContent, not innerText: innerText needs an up-to-date layout
        const drawerTitleSource = document.getElementById(`wt-drawer-content-title-${blockId}`).textContent;
        const drawerBodySource = document.getElementById(`wt-drawer-content-body-${blockId}`).innerHTML;

        this.drawerTitle.textContent = drawerTitleSource;
        this.drawerBody.innerHTML = drawerBodySource;
        // The video sits in a <template> so it is not fetched at page load.
        // Template content lives in an inert document: WebKit does not run
        // source selection for a <video> created there, even after it is moved
        // into the page, so import it, then load() and play() explicitly.
        this.drawerBody
          .querySelectorAll("template[data-drawer-video]")
          .forEach((tpl) => tpl.replaceWith(document.importNode(tpl.content, true)));
        this.drawerBody.querySelectorAll("video").forEach((video) => {
          video.muted = true;
          video.load();
          video.play().catch(() => {});
        });
      }

      closeDrawer() {
        if (this.lastTrigger) {
          this.lastTrigger.focus();
        }

        this.removeAttribute("open");
        this.isOpen = false;
        this.drawerBody?.querySelectorAll("video").forEach((video) => video.pause());
        this.drawer.setAttribute("inert", "");
        this.drawer.setAttribute("aria-hidden", "true");
        this.drawer.classList.remove(this.classDrawerActive);
        document.body.classList.remove(this.activeOverlayBodyClass);
        document.removeEventListener("keydown", this.handleKeydown);
        
        if (this.closeBtn) {
        this.closeBtn.removeEventListener("click", this.handleTriggerClick);
        }

        if (this.lastTrigger) {
          this.lastTrigger.setAttribute("aria-expanded", "false");
        }

        const overlay = this.getOverlay();
        if (overlay) {
          overlay.setAttribute("aria-hidden", "true");
        }

        this.temporaryHideFocusVisible();
      }

      setupDrawerOverlay(){
        if (!document.querySelector(`.${this.pageOverlayClass}`)) {
            this.overlay = document.createElement('div');
            this.overlay.className = `${this.pageOverlayClass}`;
            document.body.appendChild(this.overlay);
        } else {
            this.overlay = document.querySelector(`.${this.pageOverlayClass}`);
        }
        this.overlay.addEventListener("click", this.handleOverlayClick);
        this.overlay.setAttribute("aria-hidden", "true");
      }

      setupDrawer(){
        if (!document.querySelector(`.${this.drawerClass}`)){
            this.drawer = document.createElement('div');
            this.drawer.className = `${this.drawerClass}`;
            document.body.appendChild(this.drawer);

            this.drawerHeader = document.createElement('header');
            this.drawerHeader.className = 'wt-drawer-content__drawer__header';

            
            this.drawerTitle = document.createElement('div');
            this.drawerTitle.className = 'wt-drawer-content__drawer__title';
            this.drawerHeader.appendChild(this.drawerTitle);

            
            this.closeBtn = document.createElement('button');
            this.closeBtn.className = 'wt-drawer-content__drawer__close';
            this.closeBtn.setAttribute('aria-label', 'Close');
            this.closeBtn.innerHTML = `<svg class="svg-icon svg-icon--close wt-cart__drawer__close" width="24" height="24" viewBox="0 0 24 24" fill="none" tabindex="0">
                <path d="M13.3828 11.7749L20.2165 4.94112C20.6612 4.49661 20.6612 3.7779 20.2165 3.33338C19.7719 2.88887 19.0532 2.88887 18.6087 3.33338L11.7749 10.1672L4.94128 3.33338C4.49656 2.88887 3.77805 2.88887 3.33354 3.33338C2.88882 3.7779 2.88882 4.49661 3.33354 4.94112L10.1672 11.7749L3.33354 18.6088C2.88882 19.0533 2.88882 19.772 3.33354 20.2165C3.55507 20.4382 3.84634 20.5496 4.13741 20.5496C4.42848 20.5496 4.71954 20.4382 4.94128 20.2165L11.7749 13.3827L18.6087 20.2165C18.8305 20.4382 19.1215 20.5496 19.4126 20.5496C19.7037 20.5496 19.9947 20.4382 20.2165 20.2165C20.6612 19.772 20.6612 19.0533 20.2165 18.6088L13.3828 11.7749Z" fill="currentColor"/>
            </svg>`;
            this.drawerHeader.appendChild(this.closeBtn);

            this.drawer.appendChild(this.drawerHeader);

            this.drawerBody = document.createElement('div');
            this.drawerBody.className = 'wt-drawer-content__drawer__body rte';
            this.drawer.appendChild(this.drawerBody);
        } else {
            this.drawer = document.querySelector(`.${this.drawerClass}`);
            this.drawerTitle = this.drawer.querySelector(".wt-drawer-content__drawer__title");
            this.drawerBody = this.drawer.querySelector(".wt-drawer-content__drawer__body.rte")
        }

        this.drawer.setAttribute("aria-hidden", "true");
        this.drawer.setAttribute("inert", "");
      }

      init() {
        this.triggers = this.querySelectorAll(
          `.wt-drawer-content__trigger`
        );
        this.triggers.forEach((trigger) => {
            trigger.addEventListener("click", this.handleTriggerClick);
        });


        this.setupDrawerOverlay();
        this.setupDrawer();
      }
    },
  );
}